import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import * as actions from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import { isOrganization } from 'pkg/api/models/group/guards';
import { useEndpoint } from 'pkg/api/use_endpoint';
import { useAuth } from 'pkg/auth/context';
import DateTime from 'pkg/datetime';
import { resume } from 'pkg/events';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import useMixedState from 'pkg/hooks/useMixedState';
import * as ls from 'pkg/local_storage';
import * as logger from 'pkg/logger';

import { useAppState } from 'components/application/state';
import { Spinner } from 'components/loaders/spinner';

const NoOp = (): void => null;

interface AccountIdentityProviderProps {
	children: ReactNode;
}

interface AccountIdentityContextState {
	refreshAccountIdentity: () => Promise<void>;

	account: models.account.Account;
	children: models.account.Account[];

	organizations: models.group.OrganizationData[];
	organization: models.group.Group;

	setOrganization: (organization: models.group.Group) => void;
	setOrganizationById: (organizationId: number) => void;
	setOrganizations: (organizations: models.group.OrganizationData[]) => void;
}

export interface OrganizationsLocalStorageData {
	// The key will be the ID of the org
	[key: string]: {
		activeMembership: models.membership.Membership;
		orgToken: string;
	};
}

const DefaultAccountIdentityContext: AccountIdentityContextState = {
	refreshAccountIdentity: async () => {},
	account: null,
	children: [],
	organizations: [],
	organization: null,
	setOrganization: NoOp,
	setOrganizationById: NoOp,
	setOrganizations: NoOp,
};

export const AccountIdentityContext =
	createContext<AccountIdentityContextState>(DefaultAccountIdentityContext);

export const useAccountIdentity = () => {
	return useContext(AccountIdentityContext);
};

export function AccountIdentityProvider({
	children,
}: AccountIdentityProviderProps): JSX.Element {
	const { logout } = useAuth();

	const re = window.location.pathname.match(
		/organization\/(?<orgIdFromUrl>\d+)/i
	);

	const orgIdFromUrl = Number.parseInt(re?.groups.orgIdFromUrl ?? '0', 10);

	const [state, setState] = useMixedState<AccountIdentityContextState>(
		DefaultAccountIdentityContext
	);

	const { setLanguage } = useAppState();

	const { isLoading, refresh } = useEndpoint<{
		account: models.account.Account;
		organizations: models.group.OrganizationData[];
	}>(endpoints.Selfs.ShowSelf(), {}, (data, response) => {
		if (!response) {
			logger.warning('failed to retrieve account', { cause: response });

			return;
		}

		if (response.status === 401) {
			// auth is no longer valid. When a stale token lands us on a public
			// registration entry point (/registration, /join, /claim), preserve
			// that URL through logout so we re-render into the public flow on the
			// same link instead of dropping the user on the home page (REG-837).
			//
			// The uuid registration category link that clubs share counts as an
			// entry point too, and so does the org-scoped admin address. The public
			// router decides where that goes: a public id on to the public link, a
			// numeric id Home.
			const { pathname, search } = window.location;
			const isPublicEntryPoint =
				/^\/(registration|join|claim)\//.test(pathname) ||
				/^\/registrations\/category\//.test(pathname) ||
				/^\/organization\/\d+\/registrations\/category\//.test(pathname);

			logout(isPublicEntryPoint ? `${pathname}${search}` : undefined);
			return;
		}

		const hasData: boolean = Object.keys(data)?.length > 0;

		actions.app.setupOrganizationLocalStorage(data.organizations || []);

		if (hasData) {
			setState(data);

			actions.notifications.fetchNotifications(data.account.id);

			logger.setUserContext(data.account.id.toString());

			DateTime.resolveDateTimeOptions();
			DateTime.setHourFormat(
				data.account.hourFormat === models.account.AccountHourFormat['12h']
					? '12h'
					: '24h'
			);

			setLanguage(data.account.languageCode);
			DateTime.setLocale(data.account.languageCode);

			if (state.organization === null && data.organizations?.length > 0) {
				const lsOrganizationId = Number.parseInt(
					ls.getItem(ls.LocalStorageKeys.ActiveOrganizationId),
					10
				);

				const localStorageOrganization = data.organizations.find(
					(o: models.group.Group) => o.id === lsOrganizationId
				);

				const organizationFromUrl = data.organizations.find(
					(o: models.group.Group) => o.id === orgIdFromUrl
				);

				let org = data.organizations[0];

				if (
					organizationFromUrl &&
					organizationFromUrl.id !== state.organization?.id
				) {
					org = organizationFromUrl;
				} else if (localStorageOrganization) {
					org = localStorageOrganization;
				}

				setOrganization(org);
			}
		}
	});

	useComponentDidMount(
		() => {
			resume.on(refresh);
		},
		() => {
			resume.off(refresh);
		}
	);

	const setOrganization = (organization: models.group.Group) => {
		if (!isOrganization(organization)) {
			throw new Error('Not an organization!');
		}

		const nextOrganizationId = organization.id;

		ls.setItem(
			ls.LocalStorageKeys.ActiveOrganizationId,
			nextOrganizationId.toString()
		);

		setState({ organization });
	};

	const setOrganizationById = (organizationId: number) => {
		if (state.organization?.id === organizationId) {
			return;
		}

		const organization = state.organizations.find(
			(o) => o.id === organizationId
		);

		if (organization) {
			if (!isOrganization(organization)) {
				throw new Error('Not an organization!');
			}

			setOrganization(organization);
		}
	};

	const setOrganizations = (organizations: models.group.OrganizationData[]) => {
		setState({ organizations });
	};

	const providedState: AccountIdentityContextState = {
		...state,
		setOrganization,
		setOrganizationById,
		setOrganizations,
		refreshAccountIdentity: refresh,
	};

	if (isLoading && !providedState.account) {
		return <Spinner center />;
	}

	return (
		<AccountIdentityContext.Provider value={providedState}>
			{children}
		</AccountIdentityContext.Provider>
	);
}
